import Box from "@mui/material/Box";
import { IconButton } from "@mui/material";
import Typography from "@mui/material/Typography";
import React from "react";
import { useTranslation } from "react-i18next";
import { ColumnBox, RowBox } from "../../../../../utils/styledBox";
import { useParamsContext } from "../../../../../context/juno/ParamsContext";
import { AspectRatio as AspectRatioType } from "../../../../../types/junoTypes";
import { getIconForRatio, getRotationForRatio, sortAspectRatios } from "../../../../../utils/aspectRatioUtils";

const AspectRatio = () => {
  const { t } = useTranslation();
  const { aspectRatio, setAspectRatio, aiEngine } = useParamsContext();

  const aspectRatiosFlux = ["1:1", "16:9", "4:3", "9:16", "3:4"];

  const aspectRatiosFluxKontext = [...aspectRatiosFlux, "21:9", "9:21"];

  const aspectRatiosIdeogram = ["1:1", "16:9", "4:3", "9:16", "3:4"];

  const aspectRatiosSD3 = ["1:1", "16:9", "3:2", "5:4", "9:16", "2:3", "4:5"];

  const renderAspectRatioIcons = () => {
    // デフォルトはaspectRatiosFluxを使用
    let aspectRatios = aspectRatiosFlux;

    // 特定のエンジンのみ個別のアスペクト比を使用
    if (["FluxProKontextMax", "FluxProKontext"].includes(aiEngine!)) {
      aspectRatios = aspectRatiosFluxKontext;
    }
    if (
      [
        "Ideogram2",
        "Ideogram2Turbo",
        "IdeogramV3Remix",
        "IdeogramV3ReplaceBackground",
        "IdeogramV3",
        "IdeogramV3Edit",
      ].includes(aiEngine!)
    ) {
      aspectRatios = aspectRatiosIdeogram;
    }
    if (["SD35", "SDUltra"].includes(aiEngine!)) {
      aspectRatios = aspectRatiosSD3;
    }

    // アスペクト比をソート
    const sortedAspectRatios = sortAspectRatios([...aspectRatios]);

    const clickButton = (e: React.MouseEvent, ratio: string) => {
      e.stopPropagation();
      e.preventDefault();
      setAspectRatio({ ...aspectRatio, [aiEngine!]: ratio as AspectRatioType });
    };

    return (
      <ColumnBox>
        <RowBox justifyContent={"space-between"}>
          <Typography variant="body2" mb={1}>
            {t("juno.input.aspectRatio.title")}
          </Typography>
          {/* <Tooltip title={t("juno.input.aspectRatio.tooltip")} placement="top">
            <IconButton size="small">
              <Info fontSize="small" />
            </IconButton>
          </Tooltip> */}
        </RowBox>

        <RowBox gap={0.2}>
          {sortedAspectRatios.map((ratio) => {
            const Icon = getIconForRatio(ratio);
            const rotate = getRotationForRatio(ratio);

            return (
              <Box key={ratio} sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                <IconButton
                  onClick={(e) => clickButton(e, ratio)}
                  color={aspectRatio[aiEngine!] === ratio ? "primary" : "default"}
                >
                  <Icon sx={{ transform: `rotate(${rotate})` }} />
                </IconButton>
                <Typography variant={"caption"} onClick={(e) => clickButton(e, ratio)} sx={{ cursor: "pointer" }}>
                  {ratio}
                </Typography>
              </Box>
            );
          })}
        </RowBox>
      </ColumnBox>
    );
  };

  const handleAspectRatioChange = (ratio: string) => {
    setAspectRatio({ ...aspectRatio, DallE3: ratio as AspectRatioType });
  };

  const selectedAspectRatio = () => {
    return aspectRatio.DallE3 || "1:1";
  };

  const handleGPTImage1AspectRatioChange = (aspect: string) => {
    setAspectRatio({ ...aspectRatio, GPTImage1: aspect as AspectRatioType });
  };

  const selectedGPTImage1AspectRatio = () => {
    return aspectRatio.GPTImage1 || "1:1";
  };

  const renderDallE3AspectRatios = () => {
    const dalleRatios = ["1:1", "16:9", "9:16"];

    return (
      <ColumnBox>
        <RowBox justifyContent={"space-between"}>
          <Typography variant="body2" mb={1}>
            {t("juno.input.aspectRatio.title")}
          </Typography>
        </RowBox>

        <RowBox gap={0.2}>
          {dalleRatios.map((ratio) => {
            const Icon = getIconForRatio(ratio);
            const rotate = getRotationForRatio(ratio);

            return (
              <Box key={ratio} sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                <IconButton
                  onClick={() => handleAspectRatioChange(ratio)}
                  color={selectedAspectRatio() === ratio ? "primary" : "default"}
                >
                  <Icon sx={{ transform: `rotate(${rotate})` }} />
                </IconButton>
                <Typography
                  variant={"caption"}
                  onClick={() => handleAspectRatioChange(ratio)}
                  sx={{ cursor: "pointer" }}
                >
                  {ratio}
                </Typography>
              </Box>
            );
          })}
        </RowBox>
      </ColumnBox>
    );
  };

  const renderGPTImage1AspectRatios = () => {
    const gptRatios = ["1:1", "3:2", "2:3"];

    return (
      <ColumnBox>
        <RowBox justifyContent={"space-between"}>
          <Typography variant="body2" mb={1}>
            {t("juno.input.aspectRatio.title")}
          </Typography>
        </RowBox>

        <RowBox gap={0.2}>
          {gptRatios.map((ratio) => {
            const Icon = getIconForRatio(ratio);
            const rotate = getRotationForRatio(ratio);

            return (
              <Box key={ratio} sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                <IconButton
                  onClick={() => handleGPTImage1AspectRatioChange(ratio)}
                  color={selectedGPTImage1AspectRatio() === ratio ? "primary" : "default"}
                >
                  <Icon sx={{ transform: `rotate(${rotate})` }} />
                </IconButton>
                <Typography
                  variant={"caption"}
                  onClick={() => handleGPTImage1AspectRatioChange(ratio)}
                  sx={{ cursor: "pointer" }}
                >
                  {ratio}
                </Typography>
              </Box>
            );
          })}
        </RowBox>
      </ColumnBox>
    );
  };

  return (
    <Box>
      {aiEngine === "DallE3"
        ? renderDallE3AspectRatios()
        : aiEngine === "GPTImage1"
          ? renderGPTImage1AspectRatios()
          : renderAspectRatioIcons()}
    </Box>
  );
};
export default AspectRatio;
